// Hand-written minimal ABIs for the Fengniao (Hummingbird) BSC contracts.
// Tuple-returning functions use named outputs so viem can destructure by name.

export const erc20Abi = [
  { type: "function", name: "symbol", stateMutability: "view", inputs: [], outputs: [{ type: "string" }] },
  { type: "function", name: "decimals", stateMutability: "view", inputs: [], outputs: [{ type: "uint8" }] },
  {
    type: "function",
    name: "balanceOf",
    stateMutability: "view",
    inputs: [{ name: "account", type: "address" }],
    outputs: [{ type: "uint256" }],
  },
] as const;

export const holderLpVaultAbi = [
  {
    type: "function",
    name: "users",
    stateMutability: "view",
    inputs: [{ name: "account", type: "address" }],
    outputs: [
      { name: "lastBalance", type: "uint256" },
      { name: "rewardDebt", type: "uint256" },
      { name: "pendingLP", type: "uint256" },
      { name: "totalClaimed", type: "uint256" },
    ],
  },
  {
    type: "function",
    name: "earned",
    stateMutability: "view",
    inputs: [{ name: "account", type: "address" }],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "getStats",
    stateMutability: "view",
    inputs: [],
    outputs: [
      { name: "totalShares", type: "uint256" },
      { name: "userCount", type: "uint256" },
      { name: "totalLPDistributed", type: "uint256" },
      { name: "contractLPBalance", type: "uint256" },
      { name: "contractBNB", type: "uint256" },
      { name: "accLPPerShare", type: "uint256" },
    ],
  },
  { type: "function", name: "minHolding", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", name: "minBNBForExecution", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", name: "taxToken", stateMutability: "view", inputs: [], outputs: [{ type: "address" }] },
  { type: "function", name: "lpToken", stateMutability: "view", inputs: [], outputs: [{ type: "address" }] },
  { type: "function", name: "register", stateMutability: "nonpayable", inputs: [], outputs: [] },
  { type: "function", name: "claim", stateMutability: "nonpayable", inputs: [], outputs: [] },
] as const;

export const lpTimeVaultAbi = [
  {
    type: "function",
    name: "users",
    stateMutability: "view",
    inputs: [{ name: "account", type: "address" }],
    outputs: [
      { name: "balance", type: "uint256" },
      { name: "startTime", type: "uint256" },
      { name: "cappedStart", type: "uint256" },
      { name: "rewardAccPaid", type: "uint256" },
      { name: "timeRewardAccPaid", type: "uint256" },
      { name: "pendingReward", type: "uint256" },
      { name: "totalClaimed", type: "uint256" },
    ],
  },
  {
    type: "function",
    name: "earned",
    stateMutability: "view",
    inputs: [{ name: "account", type: "address" }],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "getStats",
    stateMutability: "view",
    inputs: [],
    outputs: [
      { name: "totalWeight", type: "uint256" },
      { name: "userCount", type: "uint256" },
      { name: "totalDistributed", type: "uint256" },
      { name: "totalReserved", type: "uint256" },
      { name: "contractBalance", type: "uint256" },
    ],
  },
  { type: "function", name: "totalWeight", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
  {
    type: "function",
    name: "userTimeWeight",
    stateMutability: "view",
    inputs: [{ name: "account", type: "address" }],
    outputs: [{ type: "uint256" }],
  },
  { type: "function", name: "token", stateMutability: "view", inputs: [], outputs: [{ type: "address" }] },
  { type: "function", name: "minLpHolding", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", name: "paused", stateMutability: "view", inputs: [], outputs: [{ type: "bool" }] },
  { type: "function", name: "register", stateMutability: "nonpayable", inputs: [], outputs: [] },
  { type: "function", name: "syncBalance", stateMutability: "nonpayable", inputs: [], outputs: [] },
  { type: "function", name: "claim", stateMutability: "nonpayable", inputs: [], outputs: [] },
] as const;

export const buybackBurnVaultAbi = [
  { type: "function", name: "pendingBNB", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", name: "active", stateMutability: "view", inputs: [], outputs: [{ type: "bool" }] },
  { type: "function", name: "pancakeOnly", stateMutability: "view", inputs: [], outputs: [{ type: "bool" }] },
  { type: "function", name: "minBNBForExecution", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", name: "minExecutionInterval", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", name: "lastExecutedAt", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", name: "totalBnbUsed", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", name: "totalTokensBurned", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", name: "taxToken", stateMutability: "view", inputs: [], outputs: [{ type: "address" }] },
] as const;

export const referralRouterAbi = [
  {
    type: "function",
    name: "buyWithBNB",
    stateMutability: "payable",
    inputs: [
      { name: "referrer", type: "address" },
      { name: "minTokenOut", type: "uint256" },
      { name: "deadline", type: "uint256" },
    ],
    outputs: [{ name: "tokenOut", type: "uint256" }],
  },
  {
    type: "function",
    name: "sellForToken",
    stateMutability: "nonpayable",
    inputs: [
      { name: "tokenOut", type: "address" },
      { name: "tokenIn", type: "uint256" },
      { name: "minOut", type: "uint256" },
      { name: "deadline", type: "uint256" },
    ],
    outputs: [{ name: "amountOut", type: "uint256" }],
  },
  {
    type: "function",
    name: "inviterOf",
    stateMutability: "view",
    inputs: [{ name: "account", type: "address" }],
    outputs: [{ type: "address" }],
  },
  {
    type: "event",
    name: "Buy",
    inputs: [
      { name: "user", type: "address", indexed: true },
      { name: "referrer", type: "address", indexed: true },
      { name: "tokenIn", type: "address" },
      { name: "amountIn", type: "uint256" },
      { name: "tokenOut", type: "uint256" },
    ],
  },
  {
    type: "event",
    name: "Bound",
    inputs: [
      { name: "user", type: "address", indexed: true },
      { name: "inviter", type: "address", indexed: true },
    ],
  },
] as const;

export const splitVaultAbi = [
  {
    type: "function",
    name: "getConfig",
    stateMutability: "view",
    inputs: [],
    outputs: [
      { name: "holderLPDividendVault", type: "address" },
      { name: "lpTimeDividendVault", type: "address" },
      { name: "buybackBurnVault", type: "address" },
      { name: "holderLPDividendBps", type: "uint256" },
      { name: "lpTimeDividendBps", type: "uint256" },
      { name: "buybackBurnBps", type: "uint256" },
    ],
  },
] as const;
